interface GetBaseEntitySchemaArgs {
  siteUrl?: string | null;
  url: string;
  name?: string | null;
  description?: string | null;
}

// Shared fields common to every page-level schema.org entity on the site,
// all of which link back to the canonical WebSite entity via @id.
const getBaseEntitySchema = ({
  siteUrl,
  url,
  name,
  description,
}: GetBaseEntitySchemaArgs) => ({
  '@context': 'https://schema.org',
  url,
  name,
  description: description || '',
  inLanguage: 'en-GB',
  isPartOf: { '@id': `${siteUrl}/#website` },
});

interface GetPageEntitySchemaArgs {
  siteUrl?: string | null;
  pageUrl?: string | null;
  pageName?: string | null;
  pageDescription?: string | null;
}

// Builds page-type schema (AboutPage/ContactPage/CollectionPage) that references
// the canonical Organization/WebSite entities declared on the home page via @id.
export const getPageEntitySchema = ({
  siteUrl,
  pageUrl,
  pageName,
  pageDescription,
}: GetPageEntitySchemaArgs) => {
  if (!siteUrl || !pageUrl) {
    return null;
  }

  const canonicalUrl = `${siteUrl}${pageUrl}`;
  const organizationId = `${siteUrl}/#organization`;
  const baseSchema = getBaseEntitySchema({
    siteUrl,
    url: canonicalUrl,
    name: pageName,
    description: pageDescription,
  });

  if (pageUrl === '/about-us/') {
    return {
      ...baseSchema,
      '@type': 'AboutPage',
      '@id': `${canonicalUrl}#aboutpage`,
      mainEntity: { '@id': organizationId },
    };
  }

  if (pageUrl === '/contact-us/') {
    return {
      ...baseSchema,
      '@type': 'ContactPage',
      '@id': `${canonicalUrl}#contactpage`,
      about: { '@id': organizationId },
    };
  }

  if (pageUrl === '/insurance-by-trade/') {
    return {
      ...baseSchema,
      '@type': 'CollectionPage',
      '@id': `${canonicalUrl}#collectionpage`,
      about: { '@id': organizationId },
      breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
    };
  }

  return null;
};

interface GetBlogEntitySchemaArgs {
  siteUrl?: string | null;
  blogUrl: string;
  blogName?: string | null;
  blogDescription?: string | null;
}

// Builds the Blog schema for the blog index page, referencing the canonical
// Organization/WebSite entities declared on the home page via @id.
export const getBlogEntitySchema = ({
  siteUrl,
  blogUrl,
  blogName,
  blogDescription,
}: GetBlogEntitySchemaArgs) => {
  if (!siteUrl) {
    return null;
  }

  return {
    ...getBaseEntitySchema({
      siteUrl,
      url: blogUrl,
      name: blogName,
      description: blogDescription,
    }),
    '@type': 'Blog',
    '@id': `${blogUrl}#blog`,
    publisher: { '@id': `${siteUrl}/#organization` },
  };
};

interface BreadcrumbPageProps {
  url?: string | null;
  name?: string | null;
}

interface GetBreadcrumbSchemaArgs {
  siteUrl?: string | null;
  pageUrl?: string | null;
  pageName?: string | null;
  parentPages?: readonly (BreadcrumbPageProps | null)[] | null;
}

// Builds a BreadcrumbList matching the visible Breadcrumb component's item
// order (ancestor pages, renamed first item to "Home", then the current page).
export const getBreadcrumbSchema = ({
  siteUrl,
  pageUrl,
  pageName,
  parentPages,
}: GetBreadcrumbSchemaArgs) => {
  if (!siteUrl || !pageUrl) {
    return null;
  }

  const items = [...(parentPages || []), { url: pageUrl, name: pageName }];

  if (items.length <= 1) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${siteUrl}${pageUrl}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: index === 0 ? 'Home' : item?.name,
      item: `${siteUrl}${item?.url ?? pageUrl}`,
    })),
  };
};

interface GetBlogPostingSchemaArgs {
  siteUrl?: string | null;
  companyName?: string | null;
  postUrl: string;
  headline?: string | null;
  description?: string | null;
  image?: string | null;
  datePublished?: string | null;
  authorName?: string | null;
}

// Builds the BlogPosting schema for an individual blog post, referencing the
// canonical Organization/WebSite entities declared on the home page via @id.
export const getBlogPostingSchema = ({
  siteUrl,
  companyName,
  postUrl,
  headline,
  description,
  image,
  datePublished,
  authorName,
}: GetBlogPostingSchemaArgs) => {
  if (!siteUrl) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${postUrl}#blogposting`,
    url: postUrl,
    headline,
    description: description || '',
    image,
    datePublished: datePublished
      ? new Date(datePublished).toISOString().split('T')[0]
      : undefined,
    author: authorName
      ? { '@type': 'Person', name: authorName }
      : {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: companyName,
          url: `${siteUrl}/`,
        },
    publisher: { '@id': `${siteUrl}/#organization` },
    isPartOf: { '@id': `${siteUrl}/#website` },
    inLanguage: 'en-GB',
  };
};
